
import { filter, mergeMap, map, catchError } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { Epic, EpicObservable } from 'interfaces/middleware'
import { selectFeedFilterActionCreator, changeSocialProfileStepActionCreator } from 'actions/feedActions'
import {
  COMPOSE_SUBMIT,
  composeSubmitActionCreator,
  feedComposeCleanUpActionCreator,
  composeBlurActionCreator,
  COMPOSE_VALIDATE,
  composeValidateActionCreator,
  composeExecCommandActionCreator,
  ComposeSubmitRequest,
} from 'actions/feedComposeActions'
import { post, patch } from 'helpers/observableAjaxHelpers'
import { Observable, merge, of, empty } from 'rxjs'
import { Store, State } from 'interfaces/state'
import { POST_FEED_POST_URL, PATCH_FEED_POST_URL, PATCH_FEED_COMMENT_URL, POST_FEED_COMMENT_URL } from 'interfaces/ajax'
import { FeedPostTargetInput, ComposeState, ComposeOutput, SelectableFeedFilterPeergroup, RawOwnComment, ComposeType } from 'interfaces/feed'
import { captureWarning, BAD_COMPOSE_STATE, BACKEND_COMPOSE_VALIDATION } from 'helpers/errorHelpers'
import { feedSelectedFilterSelector, createFeedFilterQuerySelector } from 'selectors/feedSelectors'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { getFeedPostAnchorId, normalizePost, normalizeOwnComment } from 'helpers/feedHelpers'
import { memberFeedInfoSelector } from 'selectors/feedSelectors'
import { Action } from 'actions'
import { composeSelector, composeImagesSelector } from 'selectors/feedComposeSelectors'
import { preparePostForApi, parseTags } from 'helpers/composeHelpers'
import { ImageId } from 'interfaces/media'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { PeergroupRelation, PeergroupRelationCollection } from 'interfaces/peergroups'
import { TAG_REQUIRED_ERROR, CONTENT_REQUIRED_ERROR, WITH_DESCRIPTION_ERROR, NO_FEED_USER_ERROR } from 'constants/feed'
import { AjaxResponse } from 'interfaces'
import { AjaxError } from 'rxjs/ajax'
import { isExpectedError, processError } from 'helpers/ajaxHelpers'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'

function getRelationId (relations: PeergroupRelationCollection, target: SelectableFeedFilterPeergroup) {
  const relation = getItemFromCollection<PeergroupRelation, 'id'>(relations, target.relationId)
  return relation ? relation.peergroupId : undefined
}

function extractOwnPost (composeState: ComposeState, state: State): ComposeOutput {
  const {
    type,
    text: composeText,
    mentions: composeMentions,
    id: composeId,
    link,
    isLinkRemoved,
  } = composeState

  const { text, mentions } = preparePostForApi(composeText, composeMentions)
  const tags = type === 'post' && text !== null ? parseTags(text) : null

  const images = composeImagesSelector(state, { composeId })
  const image_ids = images.reduce<ImageId[]>((acc, imageState) => {
    if (imageState.image) {
      acc.push(imageState.image.id)
    }
    return acc
  }, [])

  return {
    image_ids,
    link_ids: link && !isLinkRemoved ? [link.id] : [],
    text: text || null,
    ...mentions ? { mention_uids: mentions.map((mention) => mention.uid) } : null,
    ...tags && tags.length ? { tags } : null,
  }
}

const composeValidationEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(COMPOSE_VALIDATE, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { composeId } = params
        const state = store.getState()

        const feedMember = memberFeedInfoSelector(state)
        const username = feedMember ? feedMember.username : ''
        if (!username) {
          return of<Action>(
            composeValidateActionCreator.fail(params, NO_FEED_USER_ERROR),
            changeSocialProfileStepActionCreator('form', composeId),
          )
        }

        const composeState = composeSelector(state, params)
        if (!composeState) {
          return empty()
        }

        const postOutput = extractOwnPost(composeState, state)

        if (!postOutput.text && !postOutput.image_ids.length && !postOutput.link_ids.length) {
          return of<Action>(
            composeValidateActionCreator.fail(params, CONTENT_REQUIRED_ERROR),
            composeExecCommandActionCreator({
              composeId,
              command: {
                type: 'focus',
              },
            })
          )
        }

        if (composeState.type === 'post' && (!postOutput.tags || !postOutput.tags.length)) {
          return of<Action>(
            composeValidateActionCreator.fail(params, TAG_REQUIRED_ERROR),
            composeExecCommandActionCreator({
              composeId,
              command: {
                type: 'focus',
              },
            })
          )
        }

        return of<Action>(
          composeValidateActionCreator.success(params, {}),
          composeSubmitActionCreator.request({
            composeId,
            postOutput,
          })
        )
      }))

const createSubmitErrorHandler = <T>(event: ComposeSubmitRequest, request: T, isEdit: boolean, type: ComposeType) => (error: AjaxError) => {
  if (error.status === 400) {
    const { response } = error.xhr

    captureWarning(BACKEND_COMPOSE_VALIDATION, {
      type,
      isEdit,
      request,
      response,
    })

    if (response.text) {
      return of(composeSubmitActionCreator.fail(event, CONTENT_REQUIRED_ERROR))
    }

    if (response.tags) {
      return of(composeSubmitActionCreator.fail(event, TAG_REQUIRED_ERROR))
    }

    if (response.non_field_errors) {
      return of(composeSubmitActionCreator.fail(event, WITH_DESCRIPTION_ERROR, response.non_field_errors))
    }
  }
  return of(composeSubmitActionCreator.fail(event, error.status))
}

const handleErrors = (errorHandler: (error: AjaxError) => Observable<Action>) => <T>(observable: Observable<T>) => observable.pipe(
  catchError((error) => {
    let errorStream = errorHandler(error)

    if (isExpectedError(error)) {
      errorStream = merge(errorStream, processError(error))
    }
    return errorStream
  }))

export const composeAction$ = (action$: EpicObservable, store: Store) =>
  action$
    .pipe(filterRequestOfType(COMPOSE_SUBMIT, 'request')).pipe(
      map((action) => {
        const { params } = action.payload
        const state = store.getState()
        const composeState = composeSelector(state, params)
        if (!composeState) {
          return null
        }

        return {
          ...params,
          composeState,
        }
      }),
      filter((event): event is NonNullable<typeof event> => event !== null))

const postCreateEpic: Epic = (action$, store: Store) =>
  composeAction$(action$, store).pipe(
    mergeMap((event) => {
      const {
        composeState: {
          type,
          isEdit,
          filter: filterLocal,
        },
        postOutput,
      } = event

      if (type !== 'post' || isEdit) {
        return empty()
      }

      const state = store.getState()
      const target = filterLocal || feedSelectedFilterSelector(state)
      if (!target) {
        return empty()
      }

      const nextTarget: FeedPostTargetInput =
        target.type === 'peergroup'
          ? {
            filter_by: 'peergroup',
            id: getRelationId(state.data.peergroups.relations, target),
          }
          : {
            filter_by: 'feedgroup',
            slug: 'everyone',
          }

      const postBody = {
        ...postOutput,
        target: nextTarget,
      }
      return post(POST_FEED_POST_URL, { body: postBody }).pipe(
        mergeMap(({ response }) => {
          const helpSelectedFeedFilterQuerySelector = createFeedFilterQuerySelector()
          const feedFilter = helpSelectedFeedFilterQuerySelector(state, { filter: target })
          if (!feedFilter) {
            return empty()
          }

          const {
            post: nextPost,
            images,
          } = normalizePost(response)

          return of<Action>(
            composeSubmitActionCreator.success(event, {
              type: 'post',
              feedFilter,
              post: nextPost,
              images,
            }),
            selectFeedFilterActionCreator(target),
            activateAnchorActionCreator(getFeedPostAnchorId(response.uid), {
              behavior: 'smooth',
              block: 'start',
            }),
          )
        }))
        .pipe(handleErrors(createSubmitErrorHandler(event, postBody, false, 'post')))
    }))

const postEditEpic: Epic = (action$, store: Store) =>
  composeAction$(action$, store).pipe(
    mergeMap((event) => {
      const {
        composeState: {
          type,
          isEdit,
          postUid,
        },
        postOutput,
      } = event

      if (type !== 'post' || !isEdit || !postUid) {
        return empty()
      }

      return patch({ url: PATCH_FEED_POST_URL, params: { uid: postUid }}, { body: postOutput }).pipe(
        map(({ response }) => {
          const {
            post: nextPost,
            comments,
            images,
          } = normalizePost(response)

          return composeSubmitActionCreator.success(event, {
            type: 'post',
            post: nextPost,
            comments,
            images,
            postUid,
            isEdit: true,
          })
        }))
        .pipe(handleErrors(createSubmitErrorHandler(event, postOutput, true, 'post')))
    }))

const commentSubmitEpic: Epic = (action$, store: Store) =>
  composeAction$(action$, store).pipe(
    mergeMap((event) => {
      const {
        composeState: {
          type,
          isEdit,
          postUid,
          commentUid,
          parentCommentUid,
          replyCommentUid,
          isOpportunityReview,
        },
        postOutput,
      } = event

      if (type !== 'comment') {
        return empty()
      }

      if (!postUid) {
        captureWarning(BAD_COMPOSE_STATE)
        return empty()
      }

      const state = store.getState()
      const member = memberFeedInfoSelector(state)
      if (!member) {
        return empty()
      }

      let request$: Observable<AjaxResponse<RawOwnComment>> | null = null
      if (isEdit && commentUid) {
        request$ = patch({ url: PATCH_FEED_COMMENT_URL, params: { id: commentUid }}, { body: postOutput })

      } else if (!isEdit) {
        const postBody = {
          ...postOutput,
          post: postUid,
          ...parentCommentUid ? { parent_uid: parentCommentUid } : {},
        }
        request$ = post(POST_FEED_COMMENT_URL, { body: postBody })
      }

      if (!request$) {
        captureWarning(BAD_COMPOSE_STATE, event)
        return empty()
      }

      return request$.pipe(
        map(({ response }) => composeSubmitActionCreator.success(event, {
          type: 'comment',
          ...normalizeOwnComment(response, postUid, member.uid),
          postUid,
          parentCommentUid,
          commentUid: commentUid || replyCommentUid,
          isEdit,
          isOpportunityReview,
        })))
        .pipe(handleErrors(createSubmitErrorHandler(event, postOutput, isEdit, 'comment')))
    }))

const successSubmitEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(COMPOSE_SUBMIT, 'success')).pipe(
      mergeMap((action) => {
        const { composeId } = action.payload.params

        return of<Action>(
          composeBlurActionCreator({ composeId }),
          feedComposeCleanUpActionCreator.request({ composeId }),
        )
      }))

export const composeSubmitEpic = combineEpics(
  composeValidationEpic,
  postCreateEpic,
  postEditEpic,
  commentSubmitEpic,
  successSubmitEpic,
)
